<script setup lang="ts" generic="T">
import type { IonItem } from '#components';
import type { SearchableFields } from '~/composables/useSearch';

interface Props<T> {
  title?: string
  items: T[] | ReadonlyArray<T>,
  searchableFields?: SearchableFields<T>,
  renderChunkSize?: number
  lines?: 'full' | 'none' | 'inset',
  resolveKey: (i: T) => number | string
  footerButtonText?: string
  class?: string
}

interface Emits<T> {
  (e: 'select', item: T): void
  (e: 'footer-button-press'): void
}

const props = withDefaults(defineProps<Props<T>>(), {
  title: '',
  renderChunkSize: 15,
  lines: 'full',
  searchableFields: () => [],
  footerButtonText: '',
  class: ''
});

const emit = defineEmits<Emits<T>>();
const searchbar = useTemplateRef('searchbar');
defineExpose({ open });
const { focusOnEnter } = useWebInputManager();
const { t } = useEkI18n();
const platformStore = usePlatformStore();

const titleText = computed(() => {
  return props.title || t('input.selectOne');
});

const isOpen = ref<boolean>(false);

const { query, results } = useSearch(() => props.items, props.searchableFields);
const { window, windowSize, isAllLoaded, incrementCount, resetWindow } = useArrayWindow(results, { incrementCount: props.renderChunkSize });

function open() {
  isOpen.value = true;
}

function close() {
  isOpen.value = false;
}

function reset() {
  resetWindow();
  query.value = '';
}

function onEnter() {
  if (!platformStore.isWeb) {
    return; 
  }

  if (props.class === '') {
    return; 
  }

  focusOnEnter('.' + props.class);
}

</script>

<template>
  <Modal
    v-model:open="isOpen"
    :title="titleText"
    animation-direction="horizontal"
    :close-override="close"
    @handle-dismiss="reset"
    @did-present="searchbar?.$el.setFocus();"
  >
    <template #subheader>
      <IonToolbar>
        <IonSearchbar
          ref="searchbar"
          v-model="query"
          class="ion-padding-top"
          inputmode="search"
          :debounce="300"
          autocapitalize="off"
          autocorrect="on"
          :placeholder="t('search.placeholder')"
          @keyup.enter="onEnter"
        />
      </IonToolbar>
    </template>

    <IonContent>
      <UserMessagesSearch
        v-if="!results.length"
        icon="fa-fa fa-light fa-magnifying-glass"
        :header="t('search.noResultsHeader')"
        :subheader="t('search.noResultsText')"
      />
      <IonList v-else>
        <template
          v-for="item in window"
          :key="resolveKey(item)"
        >
          <slot
            name="custom"
            :item="item"
            :select="() => {emit('select', item); close()}"
          >
            <IonItem
              button
              :lines="lines"
              class="no-pointer"
              @click="emit('select', item); close()"
            >
              <slot
                name="item"
                :item="item"
              />
            </IonItem>
          </slot>
        </template>
        <InputInfiniteScroll
          v-model="windowSize"
          :increment-count="incrementCount"
          :disabled="isAllLoaded"
        />
      </IonList>
    </IonContent>
    <IonFooter v-if="props.footerButtonText">
      <ButtonsRegular
        :button-text="props.footerButtonText"
        type="cancel"
        @click="emit('footer-button-press'); close()"
      />
    </IonFooter>
  </Modal>
</template>

<style scoped>
.no-pointer :slotted(*) {
  pointer-events: none;
}

.no-data {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-top: 5rem;
  gap: 2rem;
}
.no-data img {
  width: 50%;
}
</style>
